import { useMemo, useState, type DragEvent, type FormEvent } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  guestDisplayName,
  needsSeat,
  type Guest,
  type SeatingPlan,
  type Table,
  type Wedding,
} from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Metric, MetricFootnote } from '@/components/ui/Stat';
import { TextInput } from '@/components/ui/Field';
import { useGuests } from '@/features/guests/queries';
import { useEntitlements } from '@/features/shell/entitlements';
import { FloorPlan } from './FloorPlan';
import {
  useAssignSeats,
  useCreateTable,
  useDeleteTable,
  useMoveTable,
  useProposeSeating,
  useSeating,
} from './queries';

/**
 * Who sits where.
 *
 * **The plan is the point of this tab now.** An earlier version of this comment
 * argued a drawn floor plan was "a printing feature" nobody needed until the
 * week before. That was wrong: arranging a room is something you do while
 * deciding, and it is a different job from putting people in chairs. The ADR it
 * came from is superseded in `docs/decisions.md`.
 *
 * What survives from it, and still governs: every assignment is available two
 * ways — drag on a pointer, a menu on the chip everywhere else; violations are
 * reported and never prevented; and a table cannot be deleted while anybody is
 * sitting at it.
 */
export function SeatingTab() {
  const { t } = useTranslation();
  const { wedding, canWrite } = useOutletContext<{ wedding: Wedding; canWrite: boolean }>();
  const wedId = wedding.wedId;

  const seating = useSeating(wedId);
  const guests = useGuests(wedId);
  const createTable = useCreateTable(wedId);
  const removeTable = useDeleteTable(wedId);
  const assign = useAssignSeats(wedId);
  const propose = useProposeSeating(wedId);
  const moveTable = useMoveTable(wedId);
  const entitlements = useEntitlements(wedId);
  const canAutoSeat = entitlements.data?.features.seatingAi ?? true;

  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState('8');
  const [plan, setPlan] = useState<SeatingPlan>();
  const [dragging, setDragging] = useState<string>();
  const [selectedId, setSelectedId] = useState<string>();

  const tables = seating.data?.tables ?? [];
  const stats = seating.data?.stats;
  const violations = seating.data?.violations ?? [];

  const seatable = useMemo(() => (guests.data ?? []).filter(needsSeat), [guests.data]);
  const byTable = useMemo(() => {
    const map = new Map<string, Guest[]>();
    for (const g of seatable) {
      if (!g.tableId) continue;
      map.set(g.tableId, [...(map.get(g.tableId) ?? []), g]);
    }
    return map;
  }, [seatable]);
  const unseated = useMemo(() => seatable.filter((g) => !g.tableId), [seatable]);

  const move = (guestId: string, tableId: string | null) =>
    assign.mutate({ assignments: [{ guestId, tableId }] });

  const onDrop = (e: DragEvent, tableId: string | null) => {
    e.preventDefault();
    const guestId = e.dataTransfer.getData('text/guest-id') || dragging;
    setDragging(undefined);
    if (guestId) move(guestId, tableId);
  };

  const addTable = (e: FormEvent) => {
    e.preventDefault();
    const seats = Number(capacity);
    if (!name.trim() || !Number.isFinite(seats) || seats < 1) return;
    createTable.mutate({ name: name.trim(), capacity: Math.min(40, Math.round(seats)) });
    setName('');
  };

  return (
    <section className="flex flex-col gap-6">
      <h1 className="text-page">{t('nav.seating')}</h1>

      <div className="panel flex flex-col gap-5 p-5">
        <div className="flex flex-wrap gap-6">
          <Metric
            label={t('seating.seated')}
            value={stats?.seated ?? 0}
            sub={t('seating.seatedSub', { total: stats?.needSeats ?? 0 })}
          />
          <Metric
            label={t('seating.unseated')}
            value={stats?.unseated ?? 0}
            sub={
              (stats?.capacity ?? 0) < (stats?.needSeats ?? 0)
                ? t('seating.notEnoughSeats')
                : t('seating.unseatedSub')
            }
          />
        </div>
        <div className="border-t border-line pt-4">
          <MetricFootnote
            items={[
              t('seating.tableCount', { count: stats?.tables ?? 0 }),
              t('seating.capacityCount', { count: stats?.capacity ?? 0 }),
            ]}
          />
        </div>
      </div>

      {/* Warnings, never refusals. A couple rearranging tables at 11pm knows
          things this code does not, so it says what looks wrong and leaves the
          decision to them. */}
      {violations.filter((v) => v.kind !== 'unseated').length > 0 && (
        <ul className="flex flex-col gap-2" role="status">
          {violations
            .filter((v) => v.kind !== 'unseated')
            .map((v) => (
              <li
                key={`${v.kind}-${v.tableId ?? ''}-${v.guestIds.join()}`}
                className="panel px-4 py-2 text-caption text-status-declinedFg"
              >
                {v.message}
              </li>
            ))}
        </ul>
      )}

      {canWrite && (
        <form className="flex flex-wrap items-center gap-2" onSubmit={addTable}>
          <label className="min-w-[10rem] flex-1">
            <span className="sr-only">{t('seating.tableName')}</span>
            <TextInput
              placeholder={t('seating.tableName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="w-24">
            <span className="sr-only">{t('seating.seats')}</span>
            <TextInput
              type="number"
              inputMode="numeric"
              min={1}
              max={40}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </label>
          <Button type="submit" variant="secondary" loading={createTable.isPending}>
            {t('seating.addTable')}
          </Button>
          {tables.length > 0 && unseated.length > 0 && canAutoSeat && (
            <Button
              variant="secondary"
              loading={propose.isPending}
              onClick={() => propose.mutate(undefined, { onSuccess: setPlan })}
            >
              {t('seating.autoSeat')}
            </Button>
          )}
        </form>
      )}

      {/* A proposal, never applied on its own: a one-press rearrangement of a
          room somebody has spent an hour on is not a feature. */}
      {plan && (
        <div className="panel flex flex-col gap-3 p-4">
          <p className="text-body">
            {t('seating.planSummary', { count: plan.assignments.length })}
          </p>
          {plan.unplaced.map((v) => (
            <p key={v.guestIds.join()} className="text-caption text-status-declinedFg">
              {v.message}
            </p>
          ))}
          <div className="flex gap-2">
            <Button
              loading={assign.isPending}
              onClick={() =>
                assign.mutate(
                  { assignments: plan.assignments },
                  { onSuccess: () => setPlan(undefined) },
                )
              }
            >
              {t('seating.applyPlan')}
            </Button>
            <Button variant="secondary" onClick={() => setPlan(undefined)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}

      {!canAutoSeat && tables.length > 0 && (
        <p className="text-caption text-brass-text">{t('seating.autoSeatPlan')}</p>
      )}

      {tables.length > 0 && <p className="text-caption text-leaf-soft">{t('seating.planHint')}</p>}

      {tables.length > 0 && (
        <FloorPlan
          tables={tables}
          bySeat={byTable}
          {...(selectedId ? { selectedId } : {})}
          onSelect={setSelectedId}
          onMove={(tableId, x, y) => moveTable.mutate({ tableId, x, y })}
        />
      )}

      {selectedId && (
        <button
          type="button"
          onClick={() => setSelectedId(undefined)}
          className="min-h-tap self-start text-caption text-leaf-soft underline underline-offset-4"
        >
          {t('seating.showAllTables')}
        </button>
      )}

      {tables.length === 0 ? (
        <EmptyState title={t('seating.emptyTitle')} body={t('seating.emptyBody')} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {(selectedId ? tables.filter((x) => x.tableId === selectedId) : tables).map((table) => (
            <TableCard
              key={table.tableId}
              table={table}
              members={byTable.get(table.tableId) ?? []}
              tables={tables}
              onDrop={(e) => onDrop(e, table.tableId)}
              onDragStart={setDragging}
              onMove={move}
              onDelete={() => removeTable.mutate({ tableId: table.tableId })}
              deleteError={removeTable.isError ? removeTable.error.message : undefined}
            />
          ))}
        </div>
      )}

      <section
        className="panel flex flex-col gap-3 p-4"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => onDrop(e, null)}
      >
        <h2 className="eyebrow">{t('seating.unseatedList', { count: unseated.length })}</h2>
        {unseated.length === 0 ? (
          <p className="text-caption text-leaf-soft">{t('seating.everyoneSeated')}</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {unseated.map((g) => (
              <Chip
                key={g.guestId}
                guest={g}
                tables={tables}
                onDragStart={setDragging}
                onMove={move}
              />
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

function TableCard({
  table,
  members,
  tables,
  onDrop,
  onDragStart,
  onMove,
  onDelete,
  deleteError,
}: {
  table: Table;
  members: Guest[];
  tables: Table[];
  onDrop: (e: DragEvent) => void;
  onDragStart: (guestId: string) => void;
  onMove: (guestId: string, tableId: string | null) => void;
  onDelete: () => void;
  deleteError?: string;
}) {
  const { t } = useTranslation();
  const over = members.length > table.capacity;

  return (
    <article
      className="panel flex flex-col gap-3 p-4"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3 className="text-section">{table.name}</h3>
        <span
          className={`tabular text-caption ${over ? 'text-status-declinedFg' : 'text-leaf-soft'}`}
        >
          {members.length}/{table.capacity}
        </span>
      </header>

      <ul className="flex min-h-[3rem] flex-wrap gap-2">
        {members.map((g) => (
          <Chip
            key={g.guestId}
            guest={g}
            tables={tables}
            onDragStart={onDragStart}
            onMove={onMove}
          />
        ))}
      </ul>

      <footer className="flex items-center justify-between gap-2 border-t border-line pt-2">
        <span className="text-caption text-leaf-soft">{t(`seating.shape.${table.shape}`)}</span>
        <button
          type="button"
          onClick={onDelete}
          className="min-h-tap text-caption text-leaf-soft underline-offset-4 hover:text-status-declinedFg hover:underline"
        >
          {t('common.remove')}
        </button>
      </footer>
      {deleteError && (
        <p role="alert" className="text-caption text-status-declinedFg">
          {deleteError}
        </p>
      )}
    </article>
  );
}

/**
 * One person. Draggable where there is a pointer, and carrying a menu
 * everywhere — a chip you can only drag is a chip a phone cannot move.
 */
function Chip({
  guest,
  tables,
  onDragStart,
  onMove,
}: {
  guest: Guest;
  tables: Table[];
  onDragStart: (guestId: string) => void;
  onMove: (guestId: string, tableId: string | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <li
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/guest-id', guest.guestId);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(guest.guestId);
      }}
      className="flex items-center gap-1 rounded-pill border border-line bg-cream pl-3 text-caption"
    >
      <span className="truncate">{guestDisplayName(guest, undefined)}</span>
      <label>
        <span className="sr-only">{t('seating.moveTo')}</span>
        <select
          value={guest.tableId ?? ''}
          onChange={(e) => onMove(guest.guestId, e.target.value || null)}
          className="min-h-tap max-w-[2.5rem] cursor-pointer border-0 bg-transparent text-caption text-leaf-soft"
        >
          <option value="">{t('seating.unseat')}</option>
          {tables.map((tbl) => (
            <option key={tbl.tableId} value={tbl.tableId}>
              {tbl.name}
            </option>
          ))}
        </select>
      </label>
    </li>
  );
}
